/**
 * Registrar Pagamento — passo 1 do ato de pagar: só a entrada do dinheiro.
 *
 * Os campos são os MESMOS do Step 1 do Registrar Recebimento, e por isso são as mesmas moléculas.
 * O que muda é o que NÃO está aqui: nenhuma seleção de operação ou fatura. Quem diz o que este
 * dinheiro paga é o time de cobrança, depois, no passo 2 — e é justamente essa separação que a
 * tela de Pagamentos existe para sustentar.
 */
import { useState } from 'react';
import { FaTimes, FaCloudUploadAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';

import PaymentDateInput, { toIsoInstant } from '../../../molecules/payment/PaymentDateInput';
import PaymentTypeFields, {
    PaymentTypeValue, emptyPaymentTypeValue, assetDationTotal,
} from '../../../molecules/payment/PaymentTypeFields';
import PayerFields, { PayerValue, emptyPayer, onlyDigits } from '../../../molecules/payment/PayerFields';
import { buildPaymentBlock, newPaymentId } from '../../../molecules/payment/paymentPayload';
import { PaymentType, PaymentMethod } from '../../../../constants/payment';
import { registerPayment, uploadPaymentReceipt } from '../services/billingApi';

const label = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const input = 'w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white';

const hoje = () => new Date().toISOString().split('T')[0];
const parseBRL = (v: string) => Number((v || '').replace(/\./g, '').replace(',', '.')) || 0;

interface Props {
    onClose: () => void;
    /** chamado após registrar, para a tela recarregar o dia */
    onRegistered: () => void;
}

const RegisterPaymentModal: React.FC<Props> = ({ onClose, onRegistered }) => {
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(hoje());
    const [time, setTime] = useState('00:00');
    const [showTime, setShowTime] = useState(false);
    const [typeFields, setTypeFields] = useState<PaymentTypeValue>(emptyPaymentTypeValue());
    const [payer, setPayer] = useState<PayerValue>(() => emptyPayer());
    const [notes, setNotes] = useState('');
    const [receiptFile, setReceiptFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    /**
     * Gerado ao ABRIR o modal e mantido enquanto ele existir: reenviar este formulário é reenviar
     * o MESMO comando, e o consumidor descarta o segundo. Gerar no submit faria de dois cliques
     * dois pagamentos.
     */
    const [paymentId] = useState(() => newPaymentId());

    const valor = parseBRL(amount);
    // mesmas condições do Step 1: valor, documento do pagador e — em pagamento financeiro — a
    // conta onde o dinheiro caiu, que é o que o painel reparte
    const podeSalvar = valor > 0
        && onlyDigits(payer.document).length >= 11
        && (typeFields.payment_type !== PaymentType.FINANCIAL || !!typeFields.receiving_bank_account);

    const salvar = async () => {
        setSaving(true);
        try {
            const paymentDate = toIsoInstant(date, time, showTime)!;  // wall-clock local → UTC
            /**
             * O comprovante sobe ANTES do registro: registrar é assíncrono (o pagamento nasce no
             * consumidor do evento), então só o upload prévio consegue mandar o nome do arquivo
             * junto do comando. O nome canônico quem monta é o backend.
             */
            let receiptName = '';
            if (receiptFile) {
                const uploaded = await uploadPaymentReceipt(
                    paymentId, receiptFile, paymentDate, payer.name,
                );
                receiptName = uploaded.fileName;
            }
            await registerPayment(buildPaymentBlock({
                paymentId,
                amount: valor,
                paymentDate,
                typeFields,
                payer,
                notes,
                receiptName,
            }));
            toast.success('Pagamento registrado. Aguardando identificação das faturas.');
            onRegistered();
            onClose();
        } catch (e: any) {
            toast.error(`Não foi possível registrar: ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <div>
                        <h3 className="text-lg font-semibold">Registrar Pagamento</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Entrada do dinheiro. A identificação das faturas é feita depois, pela cobrança.
                        </p>
                    </div>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                <div className="px-6 py-4 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={label} htmlFor="registrar-pagamento-data">Data do Pagamento *</label>
                            <PaymentDateInput date={date} time={time} showTime={showTime} className={input}
                                onChange={next => { setDate(next.date); setTime(next.time); setShowTime(next.showTime); }} />
                        </div>
                        <div>
                            <label className={label} htmlFor="registrar-pagamento-valor">Valor Recebido (R$) *</label>
                            <input id="registrar-pagamento-valor" type="text" value={amount}
                                onChange={e => setAmount(e.target.value)}
                                placeholder="2.500.000,00" className={`${input} text-lg font-semibold`} />
                        </div>
                    </div>

                    <div className="mt-4">
                        <PaymentTypeFields
                            value={typeFields}
                            onChange={next => {
                                // dação: o valor recebido acompanha o total dos bens
                                const total = assetDationTotal(next.asset_dation_list);
                                if (next.payment_type === PaymentType.ASSET_DATION
                                    && total !== assetDationTotal(typeFields.asset_dation_list)) {
                                    setAmount(total.toFixed(2).replace('.', ','));
                                }
                                setTypeFields(next);
                            }}
                            methods={[PaymentMethod.PIX, PaymentMethod.TRANSFER, PaymentMethod.BOLETO]}
                            labelClassName={label}
                            inputClassName={input}
                            idPrefix="registrar-pagamento"
                        />
                    </div>

                    <div className="mt-6 border-t dark:border-slate-700 pt-4">
                        <PayerFields value={payer} onChange={setPayer}
                            labelClassName={label} inputClassName={input} />
                    </div>

                    <div className="mt-6 border-t dark:border-slate-700 pt-4">
                        <label className={label}>Comprovante de pagamento (opcional)</label>
                        <label className="flex items-center gap-2 border border-dashed dark:border-slate-600 rounded px-3 py-3 text-sm text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700">
                            <FaCloudUploadAlt /> {receiptFile?.name || 'Selecionar arquivo PDF'}
                            <input type="file" accept="application/pdf" className="hidden"
                                onChange={e => setReceiptFile(e.target.files?.[0] || null)} />
                        </label>
                    </div>

                    <div className="mt-4">
                        <label className={label} htmlFor="registrar-pagamento-obs">Observações</label>
                        <input id="registrar-pagamento-obs" type="text" value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Referência bancária, NF, etc." className={input} />
                    </div>
                </div>

                <div className="px-6 py-4 border-t dark:border-slate-600 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                        Cancelar
                    </button>
                    <button onClick={salvar} disabled={!podeSalvar || saving}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">
                        {saving ? 'Registrando...' : 'Registrar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RegisterPaymentModal;
